import { useMemo } from 'react';
import type { SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import pageOverrides from '../pages.json';

export const SISTER_ALIAS = 'sister-properties';
export const SISTER_PATH = '/sister-communities';

/**
 * Peek-only definitions for reading SisterProperties community slot groups.
 * Field shapes and defaultValues stay aligned with SisterProperties.tsx so
 * thank-you / nav helpers see the same baked sister communities.
 */
const creekviewPeekDef: SlotGroupDefinition = {
  label: 'Creekview Apartments',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_limit,w_1200/s3/2/5458/creekview%20-%20pool%20with%20people.png',
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Creekview Apartments' },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.creekviewapts.com/' },
  },
};

const ovationPeekDef: SlotGroupDefinition = {
  label: 'Ovation at Lewisville',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_limit,w_1200/s3/2/5458/34-2250%20s%20valley%20parkway-22.jpg',
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'Ovation at Lewisville Apartment Homes',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.ovationatlewisville.com/' },
  },
};

const cypressPeekDef: SlotGroupDefinition = {
  label: 'Cypress at Lewisville',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_limit,w_1200/s3/2/5458/dji_20240404131947_0316_d.jpg',
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'Cypress at Lewisville Apartment Homes',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.cypressatlewisville.com/' },
  },
};

const clairbornePeekDef: SlotGroupDefinition = {
  label: 'The Clairborne',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_limit,w_1200/s3/2/5458/04-2355%20n%20hwy%20360%20-6%20copy.jpg',
    },
    name: {
      label: 'Community Name',
      type: 'text',
      defaultValue: 'The Clairborne Apartment Homes',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.clairborneapts.com/' },
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isSisterCommunityFilled(community: Record<string, string | null>): boolean {
  return hasText(community.name) || hasText(community.url) || hasText(community.image);
}

/** Page is eligible when visible and still hosts the SisterProperties section. */
export function isSisterPropertiesPageActive(): boolean {
  const page = (
    pageOverrides as Record<
      string,
      { visible?: boolean; path?: string; sections?: { slot?: string }[] }
    >
  )[SISTER_ALIAS];
  if (!page || page.visible === false) return false;
  // Property sites may only ship nav metadata in pages.json (no sections).
  // Absence of a local sections override means the inherited composition
  // still includes SisterProperties.
  if (!page.sections) return true;
  return page.sections.some((section) => section.slot === 'SisterProperties');
}

/**
 * True when at least one Sister Properties community card has content.
 * Used for thank-you CTA defaults (point at sister properties only when
 * there is something to explore). Nav / sitemap use
 * `isSisterPropertiesPageActive()` instead so the link appears whenever
 * the page itself is on.
 */
export function useHasSisterProperties(): boolean {
  const creekview = useSlotGroup('sisterproperties.creekview', creekviewPeekDef);
  const ovation = useSlotGroup('sisterproperties.ovation', ovationPeekDef);
  const cypress = useSlotGroup('sisterproperties.cypress', cypressPeekDef);
  const clairborne = useSlotGroup('sisterproperties.clairborne', clairbornePeekDef);

  return useMemo(
    () =>
      isSisterPropertiesPageActive() &&
      [creekview, ovation, cypress, clairborne].some(isSisterCommunityFilled),
    [creekview, ovation, cypress, clairborne],
  );
}
